import React from 'react';
import { Employee, Branch } from '../../types';
import { PageHeader } from '../../components/Common';
import { RaaftFormState } from './templates';

interface GeneratorFormProps {
  form: RaaftFormState;
  setForm: React.Dispatch<React.SetStateAction<RaaftFormState>>;
  vessels: Branch[];
  employees: Employee[];
  generatedPreview: string;
  onGeneratePreview: () => void;
  onPrintLetter: () => void;
  onClose: () => void;
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

const RaaftGeneratorForm: React.FC<GeneratorFormProps> = ({
  form, setForm, vessels, employees, generatedPreview, onGeneratePreview, onPrintLetter, onClose,
}) => {
  const selectedEmps = employees.filter(e => form.selectedEmployeeIds.includes(e.id));

  const handleToggleEmployee = (empId: string) => {
    setForm(prev => ({
      ...prev,
      selectedEmployeeIds: prev.selectedEmployeeIds.includes(empId)
        ? prev.selectedEmployeeIds.filter(id => id !== empId)
        : [...prev.selectedEmployeeIds, empId],
    }));
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Immigration Sign Off - Generate Letter" description="طلب رفت بحارة - تأشيرة خروج"
        actions={[{ label: 'Back', onClick: onClose, variant: 'info' }]}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">Vessel &amp; Letter Info</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">مكتب الجوازات (Passport Office)</label>
            <input type="text" value={form.passportOffice} onChange={e => setForm(prev => ({ ...prev, passportOffice: e.target.value }))} placeholder="بورسعيد" className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vessel</label>
            <select value={form.vesselId} onChange={e => setForm(prev => ({ ...prev, vesselId: e.target.value }))} className={inputClass}>
              <option value="">-- Select Vessel --</option>
              {vessels.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">العلم (Flag)</label>
              <input type="text" value={form.flag} onChange={e => setForm(prev => ({ ...prev, flag: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">جنسية المالك (Owner Nationality)</label>
              <input type="text" value={form.ownerNationality} onChange={e => setForm(prev => ({ ...prev, ownerNationality: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">من ميناء (From Port)</label>
              <input type="text" value={form.fromPort} onChange={e => setForm(prev => ({ ...prev, fromPort: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">المتواجدة حاليا (Current Location)</label>
              <input type="text" value={form.currentLocation} onChange={e => setForm(prev => ({ ...prev, currentLocation: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Select Employees ({form.selectedEmployeeIds.length} selected)</h3>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md divide-y">
              {employees.length === 0 && <div className="p-3 text-sm text-gray-500 text-center">No employees found</div>}
              {employees.map(emp => (
                <label key={emp.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={form.selectedEmployeeIds.includes(emp.id)} onChange={() => handleToggleEmployee(emp.id)} className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-3" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{emp.name}</span>
                    <span className="text-xs text-gray-500 ml-2">({emp.position})</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onGeneratePreview} disabled={!form.passportOffice || form.selectedEmployeeIds.length === 0} className="flex-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">Generate</button>
            {generatedPreview && <button onClick={onPrintLetter} className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700">Print</button>}
          </div>
        </div>
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">Preview</h2>
          {generatedPreview ? (
            <div className="border rounded-md overflow-auto bg-white" style={{ minHeight: 400 }}>
              <iframe srcDoc={generatedPreview} title="Letter Preview" className="w-full border-0" style={{ minHeight: 600 }} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 py-20" dir="rtl">
              <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm">قم بتعبئة البيانات واضغط &quot;Generate&quot; لمعاينة الخطاب</p>
              {selectedEmps.length > 0 && <p className="text-xs mt-1">{selectedEmps.length} بحار مختار</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RaaftGeneratorForm;
